import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { portalApi } from "@/lib/api";
import { Card, Loading, Empty } from "@/components/Shared";
import { ProductThumb } from "@/pages/QuoteBuilder";
import { StatusPill } from "@/components/DocParts";
import { formatINR, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ClipboardList, ArrowLeft, Receipt } from "lucide-react";

const poPill = (s) => (s === "Submitted" ? "Sent" : s === "Accepted" ? "Paid" : "Rejected");
const poLabel = (s) => (s === "Submitted" ? "Awaiting acceptance" : s);

export function PortalOrders() {
  const [rows, setRows] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { portalApi.get("/portal/purchase-orders").then((r) => setRows(r.data)); }, []);
  return (
    <div data-testid="portal-orders">
      <h1 className="text-2xl sm:text-3xl font-bold text-[#101D51] tracking-tight mb-4">My purchase orders</h1>
      {!rows ? <Loading /> : rows.length === 0 ? <Empty icon={ClipboardList} title="No purchase orders yet" hint="Submit your first PO from the cart." /> : (
        <Card className="divide-y divide-[#F0F1F5]">
          {rows.map((p) => (
            <button key={p.id} onClick={() => navigate(`/portal/orders/${p.id}`)} className="w-full text-left p-3 flex items-center gap-2 sm:gap-3 hover:bg-[#F5F6FA] min-h-[56px]" data-testid={`portal-po-${p.number}`}>
              <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-[#101D51] truncate">{p.number} {p.retailer_ref && <span className="text-[#6B7189] font-normal">· ref {p.retailer_ref}</span>}</div><div className="text-[11px] text-[#6B7189]">{formatDate(p.date)} · {poLabel(p.status)}</div></div>
              <StatusPill status={poPill(p.status)} />
              <div className="w-20 sm:w-28 text-right font-semibold text-[#101D51] text-sm">{formatINR(p.grand_total)}</div>
            </button>))}
        </Card>)}
    </div>);
}

export function PortalOrderDetail() {
  const { id } = useParams(); const navigate = useNavigate();
  const [po, setPo] = useState(null);
  useEffect(() => { portalApi.get(`/portal/purchase-orders/${id}`).then((r) => setPo(r.data)); }, [id]);
  if (!po) return <Loading />;
  return (
    <div data-testid="portal-po-detail">
      <Button variant="ghost" className="mb-2 -ml-2 text-[#6B7189]" onClick={() => navigate("/portal/orders")}><ArrowLeft className="w-4 h-4" /> My orders</Button>
      <div className="flex items-center gap-2 flex-wrap mb-4"><h1 className="text-2xl font-bold text-[#101D51]">{po.number}</h1><StatusPill status={poPill(po.status)} testid="portal-po-status" /><span className="text-sm text-[#6B7189]">{poLabel(po.status)} · {formatDate(po.date)}</span></div>
      {po.status === "Accepted" && po.order && <Card className="p-3 mb-4 text-sm bg-[#0B7C5A]/5 border-[#0B7C5A]/30 text-[#0B7C5A]" data-testid="portal-po-accepted">Accepted by your distributor — sales order <b>{po.order.number}</b> ({po.order.status}).</Card>}
      {po.status === "Rejected" && <Card className="p-3 mb-4 text-sm bg-[#F83A84]/5 border-[#F83A84]/30 text-[#F83A84]">Rejected{po.reject_reason ? ` — ${po.reject_reason}` : ""}.</Card>}
      <Timeline po={po} />
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 divide-y divide-[#F0F1F5]" data-testid="portal-po-lines">
          {po.lines.map((l) => (
            <div key={l.id} className="p-3 flex items-center gap-3">
              <ProductThumb url={l.image_url} size={10} />
              <div className="min-w-0 flex-1"><div className="text-sm font-medium text-[#101D51] truncate">{l.name}</div><div className="text-[11px] text-[#6B7189]">{l.code} · {l.quantity} {l.unit} × {formatINR(l.unit_price)} · GST {Math.round(l.gst_rate * 100)}%</div></div>
              <div className="text-sm font-semibold text-[#101D51]">{formatINR(l.total)}</div>
            </div>))}
        </Card>
        <Card className="p-4 text-sm text-[#101D51] self-start">
          {po.retailer_ref && <div className="flex justify-between py-1"><span className="text-[#6B7189]">Your reference</span><span>{po.retailer_ref}</span></div>}
          <div className="flex justify-between py-1"><span className="text-[#6B7189]">Taxable value</span><span>{formatINR(po.taxable_total)}</span></div>
          <div className="flex justify-between py-1"><span className="text-[#6B7189]">GST</span><span>{formatINR(po.tax_total)}</span></div>
          <div className="flex justify-between py-2 mt-1 border-t border-[#101D51] font-bold text-lg"><span>Total</span><span data-testid="portal-po-total">{formatINR(po.grand_total)}</span></div>
          {po.notes && <div className="text-xs text-[#6B7189] mt-2">Notes: {po.notes}</div>}
        </Card>
      </div>
    </div>);
}

// Vertical order-status timeline: Submitted → Accepted/Rejected → Sales order progress.
function Timeline({ po }) {
  const steps = [{ label: "PO submitted", at: po.date || po.created_at, done: true }];
  const hist = po.status_history || [];
  const ev = (s) => hist.find((h) => h.status === s);
  if (po.status === "Rejected") steps.push({ label: "Rejected by distributor", at: ev("Rejected")?.at, done: true, bad: true, note: po.reject_reason });
  else {
    steps.push({ label: "Accepted by distributor", at: ev("Accepted")?.at, done: po.status === "Accepted" });
    const o = po.order;
    const oi = o ? ["Confirmed", "In Fulfilment", "Delivered", "Invoiced"].indexOf(o.status) : -1;
    ["Order confirmed", "Being packed", "Delivered", "Invoiced"].forEach((label, i) => steps.push({ label, done: oi >= i, at: null }));
  }
  return (
    <Card className="p-4 mb-4" data-testid="portal-po-timeline">
      <div className="text-xs font-semibold uppercase tracking-wide text-[#6B7189] mb-3">Order status</div>
      <ol className="relative border-l-2 border-[#E6E8F0] ml-2 space-y-4">
        {steps.map((s, i) => (
          <li key={s.label} className="ml-4 relative" data-testid={`timeline-step-${i}`}>
            <span className={`absolute -left-[23px] top-0.5 w-3.5 h-3.5 rounded-full border-2 ${s.bad ? "bg-[#F83A84] border-[#F83A84]" : s.done ? "bg-[#0B7C5A] border-[#0B7C5A]" : "bg-white border-[#C7CBDA]"}`} />
            <div className={`text-sm ${s.done ? "font-semibold text-[#101D51]" : "text-[#9AA0B5]"}`}>{s.label}</div>
            {s.at && <div className="text-[11px] text-[#6B7189]">{formatDate(s.at)}</div>}
            {s.note && <div className="text-xs text-[#F83A84] mt-0.5">{s.note}</div>}
          </li>))}
      </ol>
    </Card>);
}

export function PortalInvoices() {
  const [rows, setRows] = useState(null);
  useEffect(() => { portalApi.get("/portal/invoices").then((r) => setRows(r.data)); }, []);
  const due = (rows || []).reduce((s, i) => s + (i.balance_due || 0), 0);
  return (
    <div data-testid="portal-invoices">
      <h1 className="text-2xl sm:text-3xl font-bold text-[#101D51] tracking-tight">Invoices</h1>
      <p className="text-sm text-[#6B7189] mt-1 mb-4">Outstanding: <b className={due > 0 ? "text-[#F83A84]" : "text-[#0B7C5A]"} data-testid="portal-invoices-due">{formatINR(due)}</b></p>
      {!rows ? <Loading /> : rows.length === 0 ? <Empty icon={Receipt} title="No invoices yet" /> : (
        <Card className="divide-y divide-[#F0F1F5]">
          {rows.map((i) => (
            <div key={i.id} className="p-3 flex items-center gap-3" data-testid={`portal-invoice-${i.number}`}>
              <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-[#101D51]">{i.number}</div><div className="text-[11px] text-[#6B7189]">{formatDate(i.date)} · due {formatDate(i.due_date)}</div></div>
              <StatusPill status={i.display_status || i.status} />
              <div className="text-right"><div className="text-sm font-semibold text-[#101D51]">{formatINR(i.grand_total)}</div>{i.balance_due > 0 && <div className="text-[11px] text-[#F83A84]">due {formatINR(i.balance_due)}</div>}</div>
            </div>))}
        </Card>)}
    </div>);
}
